import React, { useEffect, useRef } from "react";
import { Button, Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { THEME } from "@/constants";
import { DropdownSelector } from "@/components/dropdown/DropDown.tsx";
import { useShallow } from "zustand/shallow";
import { useMajorProjectStore } from "@/stores/majorProjectStore.ts";
import { TEXT_APPLY, TEXT_CLEAR, TEXT_COMPANY, TEXT_FILTERS, TEXT_PROJECTNAME } from "@/constants/strings";
import { getFiltersByIds } from "@/utils/filtersUtil.ts";

interface MajorProjectFilterProps {
  isVisible: boolean;
  onCancel: () => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
}

export const MajorProjectFilter: React.FC<MajorProjectFilterProps> = ({ isVisible, onCancel, onClearFilters, onApplyFilters }) => {
  const { filters, tempFilters, companyId__in, setCompanyIdIn, id__in, setIdIn, setTempFilters, setNameIn } = useMajorProjectStore(
    useShallow(state => ({
      filters: state.filters,
      tempFilters: state.tempFilters,
      setTempFilters: state.setTempFilters,
      companyId__in: state.companyId__in,
      setCompanyIdIn: state.setCompanyIdIn,
      id__in: state.id__in,
      setIdIn: state.setIdIn,
      setNameIn: state.setNameIn
    }))
  );

  // Store the original state when modal opens to restore on cancel
  const originalStateRef = useRef<{
    companyId__in: string[];
    id__in: string[];
    name__in: string[];
    tempFilters: typeof tempFilters;
  } | null>(null);

  // Effect to handle modal open/close logic
  useEffect(() => {
    if (isVisible) {
      // Store current state when modal opens
      originalStateRef.current = {
        companyId__in: [...companyId__in],
        id__in: [...id__in],
        name__in: [...(filters.projectNames?.filter(p => id__in.includes(p.id)).map(p => p.displayName) || [])],
        tempFilters: { ...tempFilters }
      };

      // Check if there are any applied filters (not all options selected)
      const allCompanyIds = filters.companies?.map(c => c.id) || [];
      const allProjectIds = filters.projectNames?.map(p => p.id) || [];

      const hasAppliedFilters =
        (companyId__in.length > 0 && companyId__in.length < allCompanyIds.length) || (id__in.length > 0 && id__in.length < allProjectIds.length);

      if (!hasAppliedFilters) {
        // Reset to all options selected if no previous filters were applied
        const allProjectNames = filters.projectNames?.map(p => p.displayName) || [];

        setCompanyIdIn(allCompanyIds);
        setIdIn(allProjectIds);
        setNameIn(allProjectNames);
        setTempFilters({
          companies: filters.companies || [],
          projectNames: filters.projectNames || []
        });
      } else {
        // Keep existing selections if filters were previously applied
        setTempFilters({
          companies: filters.companies || [],
          projectNames: filters.projectNames || []
        });
      }
    }
  }, [isVisible, filters.companies, filters.projectNames]);

  const clearFilters = () => {
    onCancel();
    onClearFilters();
  };

  const applyFilters = () => {
    onCancel();
    onApplyFilters();
  };

  const handleCancel = () => {
    // Restore original state when canceling without applying
    if (originalStateRef.current) {
      setCompanyIdIn(originalStateRef.current.companyId__in);
      setIdIn(originalStateRef.current.id__in);
      setNameIn(originalStateRef.current.name__in);
      setTempFilters(originalStateRef.current.tempFilters);
    }
    onCancel();
  };

  const handleSelect = (selectedIds: string[]) => {
    const allProjectNames = filters.projectNames || [];

    let filteredProjectNames;

    if (selectedIds.length === 0) {
      filteredProjectNames = allProjectNames;
      setIdIn([]);
    } else {
      filteredProjectNames = getFiltersByIds(selectedIds, allProjectNames, true);
      setIdIn(filteredProjectNames.map(project => project.id));
    }
    // setTempFilters({
    //   companies: filters.companies,
    //   projectNames: filteredProjectNames
    // });
    setCompanyIdIn(selectedIds);
    setNameIn(filteredProjectNames.map(project => project.displayName));
  };

  const handleSelectProject = (selectedIds: string[]) => {
    const allProjects = filters.projectNames || [];
    const allCompanies = filters.companies || [];

    if (selectedIds.length === 0) {
      setIdIn([]);
      setCompanyIdIn([]);
      return;
    }

    const selectedProjects = allProjects.filter(project => selectedIds.includes(project.id));
    const companyIds = selectedProjects.flatMap(project => project.companyIds || []);

    // Filter companies to only those that match any selected project's company
    const filteredCompanies = getFiltersByIds(companyIds, allCompanies, false);

    setIdIn(selectedIds);
    setCompanyIdIn(filteredCompanies.map(company => company.id));
    setNameIn(selectedProjects.map(item => item.displayName));
  };

  return (
    <Modal
      open={isVisible}
      title={<span className="text-lg font-semibold text-gray-900">{TEXT_FILTERS}</span>}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={clearFilters} style={{ height: "40px" }}>
          {TEXT_CLEAR}
        </Button>,
        <Button key="apply" type="primary" onClick={applyFilters} style={{ backgroundColor: THEME.COLORS.PRIMARY, height: "40px" }}>
          {TEXT_APPLY}
        </Button>
      ]}
      styles={{
        body: {
          paddingTop: 24
        },
        footer: {
          paddingTop: 24
        }
      }}
      centered
      closeIcon={<CloseOutlined className="text-gray-700 cursor-pointer" />}
      width={400}
      wrapClassName="bottom-modal-wrapper"
      maskClosable={false}
    >
      <div>
        <div>
          <DropdownSelector title={TEXT_COMPANY} options={filters.companies} selected={companyId__in} onSelect={ids => handleSelect(ids)} />
        </div>

        <div className="mt-3">
          <DropdownSelector
            title={TEXT_PROJECTNAME}
            options={tempFilters.projectNames}
            selected={id__in}
            onSelect={selectedProject => handleSelectProject(selectedProject)}
          />
        </div>
      </div>
    </Modal>
  );
};
